/*
  HHC Free Gift — auto-adds / removes the free gift line in the cart.
  The gift is a separate $0.00 product ("Free Gift - Peppermint Balm .5 oz"),
  so NO discount is needed and the volume discounts apply to every unit.
  (Updated 9 Oct: replaced the "Free Lip Balm $50+" Buy X Get Y discount.)

  Rules (Erin, 24 Sep):
  - Gift only when the order total AFTER discounts is >= threshold ($50)
  - The gift product itself never counts toward the threshold
  - Gift is added as a one-time item (no selling plan), so subscription
    renewals won't include it -> subscribers only get it on the first order
  - Max 1 gift per order
  - If the customer removes the gift, don't re-add it this session
  - Any line of the gift product counts as the gift (even if added some other way),
    so it is removed below the threshold and kept to 1

  Speed (OCU cart drawer):
  - Gift requests go through the page's normal fetch, so the Zipify OCU cart drawer
    sees them and refreshes itself straight away (it watches cart requests).
  - Refresh events are sent for OCU / Dawn / other apps.
  - No extra /cart.js request after adding.
*/
(function () {
  var cfgEl = document.getElementById('hhc-free-gift-config');
  if (!cfgEl) return;

  var cfg;
  try {
    cfg = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }
  if (!cfg.enabled || !cfg.variantId || !cfg.threshold) return;

  var GIFT_PROP = '_hhc_free_gift';
  var DECLINED_KEY = 'hhcFreeGiftDeclined';
  var INTERNAL_HEADER = 'X-HHC-Free-Gift'; // marks our own requests so we don't react to them
  var SECTIONS = 'cart-drawer,cart-icon-bubble';
  var CART_URL_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  var running = false;
  var pending = false;
  var timer = null;
  var knownCart = null; // latest cart we have seen, used to add the gift in the same request

  /* ---------- helpers ---------- */

  function isGift(item) {
    // Tagged gift line, or any line of the $0 gift product
    return (item.properties && item.properties[GIFT_PROP]) || item.product_id === cfg.productId;
  }

  function isExcluded(item) {
    // The gift product never counts toward the threshold
    return item.product_id === cfg.productId;
  }

  function qualifyingTotal(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (!isExcluded(item)) total += item.final_line_price; // after line/product discounts
    });
    (cart.cart_level_discount_applications || []).forEach(function (d) {
      total -= d.total_allocated_amount; // after order-level discounts
    });
    return Math.max(total, 0);
  }

  function isDeclined() {
    try {
      return sessionStorage.getItem(DECLINED_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function setDeclined(value) {
    try {
      if (value) sessionStorage.setItem(DECLINED_KEY, '1');
      else sessionStorage.removeItem(DECLINED_KEY);
    } catch (e) {}
  }

  function headersOf(init) {
    var h = init && init.headers;
    if (!h) return null;
    if (typeof h.get === 'function') return h.get(INTERNAL_HEADER);
    return h[INTERNAL_HEADER];
  }

  // Uses the CURRENT window.fetch (OCU's wrapper included) so OCU sees the gift change
  function postJSON(path, body) {
    var headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    headers[INTERNAL_HEADER] = '1';
    return window
      .fetch(root + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers,
        body: JSON.stringify(body),
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      });
  }

  /* ---------- UI refresh ---------- */

  function isCartPage() {
    return /\/cart\/?$/.test(window.location.pathname);
  }

  function sectionInner(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var el = doc.querySelector(selector);
    return el ? el.innerHTML : null;
  }

  // Tell every cart UI on the page (OCU drawer, Dawn, other apps) that the cart changed
  function announce(cart) {
    var detail = { cart: cart, source: 'hhc-free-gift' };
    ['cart:refresh', 'cart:updated', 'cart:change', 'cart-drawer:refresh', 'dispatch:cart-drawer:refresh'].forEach(
      function (name) {
        try {
          document.dispatchEvent(new CustomEvent(name, { detail: detail, bubbles: true }));
        } catch (e) {}
      }
    );
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'hhc-free-gift', cartData: cart });
    }
  }

  function render(sections, cart) {
    if (isCartPage()) {
      window.location.reload();
      return;
    }

    // Dawn drawer + cart count bubble (kept in sync in case OCU is turned off)
    if (sections && sections['cart-drawer']) {
      var target = document.querySelector('#CartDrawer .drawer__inner');
      var html = sectionInner(sections['cart-drawer'], '#CartDrawer .drawer__inner');
      if (target && html !== null) target.innerHTML = html;
      var drawer = document.querySelector('cart-drawer');
      if (drawer && cart) drawer.classList.toggle('is-empty', cart.item_count === 0);
    }
    if (sections && sections['cart-icon-bubble']) {
      var bubble = document.getElementById('cart-icon-bubble');
      var bubbleHtml = sectionInner(sections['cart-icon-bubble'], '.shopify-section');
      if (bubble && bubbleHtml !== null) bubble.innerHTML = bubbleHtml;
    }

    announce(cart);
  }

  /* ---------- quantity changes in the cart drawer: fix the gift in the same round ---------- */

  // What the gift needs for this cart: {add: true}, {updates: {...}} or null
  function giftPlanFor(cart) {
    if (!cart || !cart.items) return null;
    if (cart.item_count === 0) setDeclined(false);
    var giftLines = cart.items.filter(isGift);
    var qualifies = qualifyingTotal(cart) >= cfg.threshold;
    if (qualifies && giftLines.length === 0 && !isDeclined() && cfg.giftAvailable !== false) return { add: true };
    var updates = {};
    var needed = false;
    giftLines.forEach(function (line, i) {
      var wanted = qualifies && i === 0 ? 1 : 0;
      if (line.quantity !== wanted) {
        updates[line.key] = wanted;
        needed = true;
      }
    });
    return needed ? { updates: updates } : null;
  }

  // "sections" the caller asked for (Dawn asks for them, OCU usually doesn't)
  function sectionsOf(body) {
    try {
      if (typeof FormData !== 'undefined' && body instanceof FormData) return body.get('sections');
      if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return body.get('sections');
      if (typeof body === 'string') {
        if (body.charAt(0) === '{') return JSON.parse(body).sections || null;
        return new URLSearchParams(body).get('sections');
      }
    } catch (e) {}
    return null;
  }

  // Apply the gift change and return the final cart (same shape the caller expects)
  function applyGiftPlan(plan, sections) {
    if (plan.add) {
      var props = {};
      props[GIFT_PROP] = 'true';
      var addBody = { items: [{ id: cfg.variantId, quantity: 1, properties: props }] };
      if (sections) addBody.sections = sections;
      return postJSON('cart/add.js', addBody).then(function (added) {
        return fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'X-HHC-Free-Gift': '1' } })
          .then(function (res) {
            return res.json();
          })
          .then(function (cart) {
            if (added && added.sections) cart.sections = added.sections;
            return cart;
          });
      });
    }
    var updBody = { updates: plan.updates };
    if (sections) updBody.sections = sections;
    return postJSON('cart/update.js', updBody); // returns the full cart (+ sections)
  }

  /* ---------- core sync ---------- */

  function sync(givenCart) {
    if (running) {
      pending = true;
      return;
    }
    running = true;

    var getCart =
      givenCart && givenCart.items
        ? Promise.resolve(givenCart) // change/update already returned the full cart
        : fetch(root + 'cart.js', { credentials: 'same-origin', headers: { 'X-HHC-Free-Gift': '1' } }).then(
            function (res) {
              return res.json();
            }
          );

    getCart
      .then(function (cart) {
        knownCart = cart;
        if (cart.item_count === 0) setDeclined(false);

        var giftLines = cart.items.filter(isGift);
        var qualifies = qualifyingTotal(cart) >= cfg.threshold;

        // 1) Qualifies, no gift yet -> add one (one-time purchase, no selling plan)
        if (qualifies && giftLines.length === 0 && !isDeclined()) {
          var props = {};
          props[GIFT_PROP] = 'true';
          return postJSON('cart/add.js', {
            items: [{ id: cfg.variantId, quantity: 1, properties: props }],
            sections: SECTIONS,
          }).then(function (data) {
            // No second /cart.js — we already know the new count
            render(data.sections, { item_count: cart.item_count + 1, items: cart.items });
          });
        }

        // 2) Doesn't qualify -> remove gift line(s)
        // 3) Qualifies but more than 1 gift -> keep exactly 1
        var updates = {};
        var needsUpdate = false;
        giftLines.forEach(function (line, i) {
          var wanted = qualifies && i === 0 ? 1 : 0;
          if (line.quantity !== wanted) {
            updates[line.key] = wanted;
            needsUpdate = true;
          }
        });

        if (needsUpdate) {
          return postJSON('cart/update.js', { updates: updates, sections: SECTIONS }).then(function (data) {
            render(data.sections, data);
          });
        }
      })
      .catch(function (err) {
        // e.g. gift out of stock — fail silently, the cart still works normally
        if (window.console) console.warn('[HHC free gift]', err);
      })
      .finally(function () {
        running = false;
        if (pending) {
          pending = false;
          schedule();
        }
      });
  }

  var lastCart = null;
  function schedule(cart) {
    if (cart && cart.items) lastCart = cart;
    clearTimeout(timer);
    timer = setTimeout(function () {
      var c = lastCart;
      lastCart = null;
      sync(c);
    }, 1); // only merges duplicate triggers
  }


  /* ---------- INSTANT: put the gift in the same add-to-cart request ---------- */

  // Bundle discount for a quantity, read from the bundle cards (3 -> 0.90, 6 -> 0.80)
  function bundleFactor(qty) {
    var card = document.querySelector('.bundles-main .card[qty="' + qty + '"]');
    var f = card ? parseFloat(card.dataset.factor) : NaN;
    if (!isNaN(f)) return f;
    return qty >= 6 ? 0.8 : qty >= 3 ? 0.9 : 1;
  }

  // Price data printed by the product page (sections/main-product.liquid)
  function priceData() {
    // From the gift snippet (any theme) or the 05/10 bundle code
    var el = document.getElementById('hhc-gift-prices') || document.getElementById('hhc-bundle-prices');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Estimated value (cents, after bundle + subscription discount) of the items being added
  function estimateAdded(items) {
    var data = priceData();
    if (!data) return null;
    var total = 0;
    for (var i = 0; i < items.length; i++) {
      var it = items[i];
      var v = data.variants[String(it.id)];
      if (!v) return null; // unknown product -> use the normal path
      var qty = parseInt(it.quantity, 10) || 1;
      var unit = it.selling_plan && v.plans[String(it.selling_plan)] != null ? v.plans[String(it.selling_plan)] : v.price;
      total += Math.round(unit * qty * bundleFactor(qty));
    }
    return total;
  }

  function shouldAddGiftWith(items) {
    if (!knownCart || isDeclined() || cfg.giftAvailable === false) return false;
    if (knownCart.items.some(isGift)) return false;
    if (items.some(function (it) { return String(it.id) === String(cfg.variantId); })) return false;
    var added = estimateAdded(items);
    if (added === null) return false;
    return qualifyingTotal(knownCart) + added >= cfg.threshold;
  }

  // Returns a new request body with the gift added, or null to leave the request alone
  function withGift(body) {
    // FormData (Dawn product form)
    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      if (!body.get('id')) return null;
      var item = {
        id: body.get('id'),
        quantity: body.get('quantity') || 1,
        selling_plan: body.get('selling_plan') || '',
      };
      if (!shouldAddGiftWith([item])) return null;

      var fd = new FormData();
      body.forEach(function (value, key) {
        if (key === 'id' || key === 'quantity' || key === 'selling_plan' || key === 'form_type' || key === 'utf8') return;
        var m = key.match(/^properties\[(.*)\]$/);
        if (m) fd.append('items[0][properties][' + m[1] + ']', value);
        else fd.append(key, value); // sections, sections_url, etc.
      });
      fd.append('items[0][id]', item.id);
      fd.append('items[0][quantity]', item.quantity);
      if (item.selling_plan) fd.append('items[0][selling_plan]', item.selling_plan);
      fd.append('items[1][id]', cfg.variantId);
      fd.append('items[1][quantity]', 1);
      fd.append('items[1][properties][' + GIFT_PROP + ']', 'true');
      return fd;
    }

    // JSON body ({id, quantity} or {items: [...]})
    if (typeof body === 'string') {
      var json;
      try {
        json = JSON.parse(body);
      } catch (e) {
        return null;
      }
      var items = json.items || (json.id ? [{ id: json.id, quantity: json.quantity, selling_plan: json.selling_plan, properties: json.properties }] : null);
      if (!items || !shouldAddGiftWith(items)) return null;
      var props = {};
      props[GIFT_PROP] = 'true';
      var out = {};
      Object.keys(json).forEach(function (k) {
        if (['id', 'quantity', 'selling_plan', 'properties', 'items'].indexOf(k) === -1) out[k] = json[k];
      });
      out.items = items.concat([{ id: cfg.variantId, quantity: 1, properties: props }]);
      return JSON.stringify(out);
    }
    return null;
  }

  /* ---------- watch every cart change (theme, bundles, upsell app, etc.) ---------- */

  var pageFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var self = this;
    var args = arguments;
    var promise;

    var giftBody = null;
    if (/\/cart\/add/.test(url) && typeof input === 'string' && init && init.body && !headersOf(init)) {
      try {
        giftBody = withGift(init.body);
      } catch (e) {
        giftBody = null;
      }
    }

    if (giftBody) {
      var giftInit = Object.assign({}, init, { body: giftBody });
      if (typeof giftBody === 'string' && init.headers && !(typeof init.headers.get === 'function')) {
        giftInit.headers = Object.assign({}, init.headers, { 'Content-Type': 'application/json' });
      }
      // If the combined add fails for any reason, send the customer's original request unchanged
      promise = pageFetch.call(self, input, giftInit).then(function (res) {
        return res.ok ? res : pageFetch.apply(self, args);
      }, function () {
        return pageFetch.apply(self, args);
      });
    } else if (/\/cart\/(change|update)/.test(url) && typeof input === 'string' && !headersOf(init)) {
      // Quantity change / remove (e.g. in the OCU drawer): fix the gift BEFORE handing the
      // cart back, so the drawer shows the gift in the same update instead of a later refresh
      promise = pageFetch.apply(this, arguments).then(function (res) {
        if (!res.ok) return res;
        return res
          .clone()
          .json()
          .then(function (cart) {
            var plan = giftPlanFor(cart);
            if (!plan) return res;
            return applyGiftPlan(plan, sectionsOf(init && init.body)).then(
              function (finalCart) {
                knownCart = finalCart;
                return new Response(JSON.stringify(finalCart), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' },
                });
              },
              function () {
                return res; // gift step failed -> caller still gets its normal result
              }
            );
          }, function () {
            return res;
          });
      });
    } else {
      promise = pageFetch.apply(this, arguments);
    }
    if (CART_URL_RE.test(url) && !headersOf(init)) {
      promise
        .then(function (res) {
          // change/update/clear return the full cart -> skip the /cart.js request
          if (res.ok && /\/cart\/(change|update|clear)/.test(url)) {
            return res
              .clone()
              .json()
              .then(schedule, function () {
                schedule();
              });
          }
          schedule();
        })
        .catch(function () {});
    }
    return promise;
  };

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_URL_RE.test(String(url))) {
      this.addEventListener('load', function () {
        schedule();
      });
    }
    return nativeOpen.apply(this, arguments);
  };

  // Also catch Dawn's own cart events (ignore the ones we send)
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event && event.source === 'hhc-free-gift') return;
      schedule();
    });
  }

  /* ---------- customer removes the gift -> respect it ---------- */

  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target.closest && e.target.closest('cart-remove-button, .cart-remove-button');
      if (btn && btn.closest('.cart-item--hhc-gift')) setDeclined(true);
    },
    true
  );

  /* ---------- run on page load ---------- */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      sync();
    });
  } else {
    sync();
  }
})();
